import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ICON } from '../../icons/iconIds';
import SelkirkIcon from '../icons/SelkirkIcon';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { documentDesignService } from '../../services/documentDesignService';
import { formatBytes } from './slotFitness';
import { formatRelative } from './BrandLibraryTab';
import DocumentReview from './DocumentReview';
import OwnerScopeToggle, { ownerLabel } from './OwnerScopeToggle';
import { STATUS_LABELS, isBusy } from './reviewLabels';

const MAX_DOCX_BYTES = 95 * 1024 * 1024;
const POLL_MS = 4000;
const SELECTED_KEY = 'documentDesign.documentId';

const DocumentsTab = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [uploading, setUploading] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedId, setSelectedId] = useState(() => Number(sessionStorage.getItem(SELECTED_KEY)) || null);
  const [removingId, setRemovingId] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [scope, setScope] = useState('mine');
  const inputRef = useRef(null);
  const showOwner = scope === 'all';

  const load = useCallback(async () => {
    try {
      const result = await documentDesignService.listDocuments(scope === 'all' ? { scope } : {});
      setDocuments(result.items);
      setViewer(result.viewer);
      setError(null);
    } catch (err) {
      console.error('❌ Error loading documents:', err);
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const changeScope = (next) => {
    setLoading(true);
    setScope(next);
  };

  useEffect(() => {
    if (!selectedId) load();
  }, [load, selectedId]);

  const anyBusy = documents.some((d) => isBusy(d.latest_status));
  useEffect(() => {
    if (!anyBusy || selectedId) return undefined;
    const handle = setInterval(load, POLL_MS);
    return () => clearInterval(handle);
  }, [anyBusy, load, selectedId]);

  const open = (id) => {
    if (id) sessionStorage.setItem(SELECTED_KEY, String(id));
    else sessionStorage.removeItem(SELECTED_KEY);
    setSelectedId(id);
  };

  const upload = async (file, documentId) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setError('Only .docx files are supported. Save older .doc files as .docx in Word first.');
      return;
    }
    if (file.size > MAX_DOCX_BYTES) {
      setError(`${file.name} is ${formatBytes(file.size)}; the upload limit is 95 MB. Compress pictures in Word (File › Compress Pictures › 220 ppi) and try again.`);
      return;
    }
    setUploading(file.name);
    setError(null);
    try {
      const result = await documentDesignService.uploadDocument(file, documentId);
      await load();
      if (!documentId) open(result.document_id);
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(null);
    }
  };

  const remove = async (doc, e) => {
    e?.stopPropagation();
    const label = doc.title || doc.source_file_name || `Document ${doc.id}`;
    const busy = isBusy(doc.latest_status || doc.status);
    const extra = busy ? '\n\nParsing is still in progress; deleting will cancel it.' : '';
    if (!window.confirm(`Remove "${label}" from the list?\n\nThis permanently deletes all versions, review changes, layout photos, and stored files.${extra}`)) {
      return false;
    }
    setRemovingId(doc.id);
    setError(null);
    try {
      await documentDesignService.deleteDocument(doc.id);
      if (Number(sessionStorage.getItem(SELECTED_KEY)) === doc.id) {
        sessionStorage.removeItem(SELECTED_KEY);
      }
      await load();
      return true;
    } catch (err) {
      setError(err.message || 'Failed to remove document');
      return false;
    } finally {
      setRemovingId(null);
    }
  };

  if (selectedId) {
    return (
      <DocumentReview
        documentId={selectedId}
        onBack={() => open(null)}
        onUploadVersion={upload}
        uploading={uploading}
        onDelete={async () => {
          let doc = documents.find((d) => d.id === selectedId);
          if (!doc) {
            try {
              const detail = await documentDesignService.getDocument(selectedId);
              const current = detail.current || detail.versions?.[0];
              doc = {
                id: selectedId,
                title: detail.title,
                source_file_name: current?.source_file_name,
                latest_status: current?.status,
              };
            } catch (err) {
              setError(err.message || 'Failed to load document');
              return;
            }
          }
          if (await remove(doc)) open(null);
        }}
      />
    );
  }

  const q = query.trim().toLowerCase();
  const visible = q
    ? documents.filter((d) => [d.title, d.project, d.source_file_name, showOwner ? ownerLabel(d) : null]
      .some((v) => v?.toLowerCase().includes(q)))
    : documents;

  return (
    <>
      <div
        className={`dd-dropzone dd-doc-dropzone ${dragOver ? 'over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files[0]); }}
        onClick={() => !uploading && inputRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        {uploading ? (
          <p>Uploading {uploading}…</p>
        ) : (
          <>
            <p><strong>Drop a Word report here</strong> or click to choose</p>
            <p className="dd-muted">.docx up to 95 MB · structure, figures and tables are extracted for review before layout</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          hidden
          onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }}
        />
      </div>

      {error && <div className="dd-error-inline">{error}</div>}

      <Card className="content-card">
        <div className="dd-results-header">
          <h2>
            {showOwner ? 'All documents' : 'My documents'} <span className="dd-muted">· {documents.length}</span>
          </h2>
          <div className="dd-header-tools">
            <OwnerScopeToggle viewer={viewer} scope={scope} onChange={changeScope} disabled={loading} />
            <input
              type="search"
              className="dd-search dd-search-sm"
              placeholder={showOwner ? 'Filter by title, project, file or owner' : 'Filter by title, project or file'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <Spinner size="large" />
            <p>Loading documents…</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon"><SelkirkIcon name={ICON.uiDocument} size={48} decorative /></div>
            <h3>{q ? 'No documents match' : showOwner ? 'No documents yet' : 'You have no documents yet'}</h3>
            <p>{q ? 'Try a different filter.' : 'Upload a Word report to start. Parsing takes a few seconds to a minute for large reports.'}</p>
          </div>
        ) : (
          <table className="dd-doc-table">
            <thead>
              <tr>
                <th>Title</th>
                {showOwner && <th>Owner</th>}
                <th>Project</th>
                <th>Version</th>
                <th>Status</th>
                <th>Size</th>
                <th>Updated</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((d) => {
                const status = d.latest_status || d.status;
                const issues = d.stats?.issues || {};
                return (
                  <tr key={d.id} onClick={() => open(d.id)} className={removingId === d.id ? 'dd-row-busy' : ''}>
                    <td>
                      <div className="dd-doc-title">{d.title}</div>
                      <div className="dd-muted">{d.source_file_name}{d.report_type ? ` · ${d.report_type}` : ''}</div>
                    </td>
                    {showOwner && <td className="dd-muted">{ownerLabel(d)}</td>}
                    <td>{d.project || <span className="dd-muted">—</span>}</td>
                    <td>v{d.version_no ?? '—'}{d.version_count > 1 ? <span className="dd-muted"> of {d.version_count}</span> : null}</td>
                    <td>
                      <span className={`dd-status ${status}`}>{STATUS_LABELS[status] || status}</span>
                      {status === 'parsed' && (issues.error || issues.warning) ? (
                        <span className="dd-muted"> · {issues.error ? `${issues.error} errors, ` : ''}{issues.warning || 0} warnings</span>
                      ) : null}
                    </td>
                    <td className="dd-muted">{d.source_size_bytes ? formatBytes(d.source_size_bytes) : '—'}</td>
                    <td className="dd-muted">{formatRelative(d.updated_at)}</td>
                    <td className="dd-row-actions">
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={removingId === d.id}
                        disabled={removingId != null && removingId !== d.id}
                        onClick={(e) => remove(d, e)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
};

export default DocumentsTab;
